import { ListControllerProps, useUnselectAll, useGetMainList, useListController } from "ra-core"
import { useEffect, useState, useCallback, useMemo } from "react"
import { SimpleOption } from "@/components/governance/search/AmbientFacilitiesContext"
import { RecPatch } from "@/dataProvider/tools"
import { TableSchemaResponseDto, FieldMappingResponseDto, SecondaryClassificationResponseDto } from "@/gen/api/governance"
import { pages, sorts } from "@/utils/paramsWithGetMainList"
import { useEventBus } from "@/contexts/EventBusContext/useEventBus"
import { E_FieldMapping, ET_FieldMapping } from "@/components/settings/encryption/FieldsMappingList.events"
import { promiseWithResolvers } from "@/utils/promiseWithResolvers"


export interface MutateEncryptionMappingsControllerProps {
  encryptMappingCtrl: MutateEncryptionMappingsContextProps,
}

export interface MutateEncryptionMappingsContextProps {
  currTable?: TableSchemaResponseDto;
  setCurrTable: (x: TableSchemaResponseDto | undefined) => void;
  fieldListCtrl: ListControllerProps<FieldMappingResponseDto & RecPatch>;
  classifications: SimpleOption[],
}



export const useMutateEncryptionMappingsController = (mode?: 'edit'|'update'): MutateEncryptionMappingsControllerProps => {
  const [states, setStates] = useState({
    currTable: undefined as TableSchemaResponseDto|undefined,
  })
  
  const fieldListCtrl = useListController<FieldMappingResponseDto & RecPatch>({ 
    resource: states.currTable ? 'fields' : 'noop', 
    basePath: '', 
    perPage: 400,
    filter: { dbId: states.currTable?.databaseId, tableId: states.currTable?.tableId } 
  })
  const { setFilters, filterValues, } = fieldListCtrl
  useEffect(() => {
    if (mode === 'update') {
      setFilters({ ...filterValues, notNil: (x: FieldMappingResponseDto) => {
        return !x.isSkipped && !x.encryptionRuleId && !x.customEncryptionRuleId
      }}, {})
    }
  }, [])
  
  const { data: classificationData } = useGetMainList<SecondaryClassificationResponseDto&RecPatch>('settings/governance/data-classification', pages(), sorts(), {})
  const classifications: SimpleOption[] = 
    useMemo(() => 
      Object.values<SecondaryClassificationResponseDto>(classificationData || {})
        .map(c => ({
          ...c, 
          key: c.secondaryClassificationId || -1, 
          text: `${c.primaryClassificationText} - ${c.name}`
        })).sort((a, b) => 
                ((a.primaryClassification || 0) - (b.primaryClassification || 0)) 
              || ((a.secondaryClassificationId || 0) - (b.secondaryClassificationId || 0))), 
      [classificationData]
    )
    
  const unselectAllFieldSkips = useUnselectAll('fields')
  
  const eventBus = useEventBus('fields-mapping-list')

  const setCurrTableWithGuard = useCallback(async (x: TableSchemaResponseDto | undefined) => {
    const { promise, resolve } = promiseWithResolvers<boolean>()
    setStates(v => {
      if (v.currTable === x || ((v.currTable && x && v.currTable.databaseId === x.databaseId && v.currTable.tableId === x.tableId))) {
        resolve(false)
      } else {
        eventBus.publish(E_FieldMapping.QueryTouched, { resolve } as ET_FieldMapping['query_touched'])
      }
      return v // only inspect the value, don't touch it
    })
    const thereIsEdit = await promise
    if (thereIsEdit) {
      const discardContinue = window.confirm('You have unsaved mapping updates, do you wish to discard the changes and switch table?')
      if (!discardContinue) return
    }
    
    unselectAllFieldSkips()
    setStates(v => {
      const tableId_0 = v.currTable?.tableId
      const tableId_1 = x?.tableId
      if (tableId_0 !== tableId_1) {
        eventBus.publish(E_FieldMapping.CurrTableChanged, { tableId_0, tableId_1 } as ET_FieldMapping['curr_table_changed'])
      }
      return ({...v, currTable: x})
    })
  }, [eventBus, unselectAllFieldSkips])

  const encryptMappingCtrl = useMemo(() => ({
    currTable: states.currTable,
    setCurrTable: setCurrTableWithGuard,
    fieldListCtrl,
    classifications,
  }), [states.currTable, setCurrTableWithGuard, fieldListCtrl, classifications])

  return {
    encryptMappingCtrl,
  }
}

export type FieldMappingCatagory = 'mapped' | 'skip' | 'pending-review'


